import { Box, Button, ErrorIcon, spacing, StatusMessage } from '@rulebooks/evidend-titan';
import { t, Trans } from '@lingui/macro';
import { GlobalErrorMessages } from 'components/GlobalError/GlobalErrorMessages';
import { AnimatePresence, motion } from 'framer-motion';
import { formatErrorReport } from 'helpers/formatErrorReport';
import { normalizeGlobalError, useGlobalError } from 'hooks/useGlobalError';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

// Ported from Titan until we can have animated floating elements.
// https://github.com/elseu/sdu-titan/blob/develop/src/animations/slideFade.ts#L26
const slideFadeBottom = {
  initial: { y: '40px', opacity: 0 },
  animate: { y: '0', opacity: 1 },
  exit: { y: '40px', opacity: 0 },
};

const Floating = styled(motion.div)`
  box-sizing: border-box;
  position: fixed;
  left: 0;
  bottom: 0;
  padding: ${spacing(6)};

  width: 100%;
  max-width: 640px;

  z-index: 1000000;
  pointer-events: none;

  & > * {
    pointer-events: auto;
  }
`;

const COPIED_RESET_MS = 2000;

const writeToClipboard = async (text: string) => {
  if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }
  if (typeof document === 'undefined') return;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'fixed';
  textarea.style.opacity = '0';
  document.body.appendChild(textarea);
  textarea.select();
  try {
    document.execCommand('copy');
  } finally {
    document.body.removeChild(textarea);
  }
};

export const GlobalError = () => {
  const [errors, setError] = useGlobalError();
  const [copied, setCopied] = useState(false);
  const copiedTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const normalized = useMemo(() => normalizeGlobalError(errors), [errors]);
  const diagnostics = normalized?.diagnostics;
  const messages = normalized?.messages;

  useEffect(() => {
    setCopied(false);
  }, [diagnostics]);

  useEffect(
    () => () => {
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
    },
    [],
  );

  const handleCopy = useCallback(async () => {
    if (!diagnostics) return;
    try {
      await writeToClipboard(formatErrorReport(diagnostics));
      setCopied(true);
      if (copiedTimeout.current) clearTimeout(copiedTimeout.current);
      copiedTimeout.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
    } catch (err) {
      console.error('Failed to copy error details', err);
    }
  }, [diagnostics]);

  return (
    <AnimatePresence>
      {normalized && (
        <Floating {...slideFadeBottom}>
          <StatusMessage
            defaultIsShown
            isCloseable
            isRounded
            isShown
            alignment="left"
            icon={<ErrorIcon />}
            labelClose={t`Sluiten`}
            type="danger"
            onClose={() => setError(undefined)}
          >
            {typeof messages === 'string' ? (
              messages
            ) : messages ? (
              <GlobalErrorMessages errors={messages} />
            ) : null}
            {diagnostics && (
              <Box pt={2}>
                <Button
                  size="S"
                  testId="global-error-copy-details"
                  variant="clear"
                  onClick={handleCopy}
                >
                  {copied ? <Trans>Gekopieerd!</Trans> : <Trans>Kopieer foutdetails</Trans>}
                </Button>
              </Box>
            )}
          </StatusMessage>
        </Floating>
      )}
    </AnimatePresence>
  );
};
